import React, { useState, useEffect } from 'react';
import './ClientDetailsCard.css';
import { Button, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from "../../utils/auth";
import Auth from '../../utils/auth';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import InfoTooltip from '../ClientDetailMain/InfoTooltip';
import OnboardingFlow from '../OnboardingFlow/OnboardingFlow';
import { formatDate } from '../../utils/dateFormat';

const formatNumberWithTwoDecimals = (number) => {
    return parseFloat(number).toFixed(2); 
};

const RadialSeparators = ({ count, style, circleRatio }) => {
    const turns = circleRatio / count;
    const offset = 0.5;

    return Array.from({ length: count }, (_, index) => (
        <div
            key={index}
            style={{
                position: "absolute",
                height: "100%",
                transform: `rotate(${(index * turns) - 0.25}turn)`
            }}
        >
            <div style={style} />
        </div>
    ));
};

const DateField = ({ label, value, onChange, caption }) => (
    <div className={`date-field-container-card${caption ? ' date-field-container-card--with-caption' : ''}`}>
        <div className="date-field-row-card">
            <h2 className="date-field-label-card">{label}:</h2>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    value={value ? dayjs(value) : null}
                    onChange={onChange}
                    format="MMM DD, YYYY"
                    className="date-picker-field-card"
                />
            </LocalizationProvider>
        </div>
        {caption && (
            <p className="date-field-caption-card">{caption}</p>
        )}
    </div>
);

const normalizeTouchpointFrequency = (value) => {
    if (!value) return "";
    const normalized = value.toLowerCase().trim();
    if (normalized === "weekly") return "Weekly";
    if (normalized === "bi-weekly" || normalized === "biweekly") return "Bi-Weekly";
    if (normalized === "monthly") return "Monthly";

    return "";
};

const getGhlSyncedValue = (value) => {
    return value === true || value === 'true' || value === 1;
};

const ClientDetailsCard = ({ client, isOpen, onClose, onClientUpdate }) => {
    const [icp, setIcp] = useState(client.icp || '');
    const [touchpointFrequency, setTouchpointFrequency] = useState(normalizeTouchpointFrequency(client.touchpointFrequency));
    const [billingStatus, setBillingStatus] = useState(client.billingStatus || '');
    const [updatedClient, setUpdatedClient] = useState(client);
    const [companyName, setCompanyName] = useState(client.companyName || null);
    const [clientRetentionScore, setClientRetentionScore] = useState(client.clientRetentionScore || null);
    const [allPrograms, setAllPrograms] = useState([]); 
    const [selectedPrograms, setSelectedPrograms] = useState([]); 
    const [programToRemove, setProgramToRemove] = useState(null); 
    const [openRemoveModal, setOpenRemoveModal] = useState(false);
    const [availableCSMs, setAvailableCSMs] = useState([]);
    const [assignedCSM, setAssignedCSM] = useState(client.csmName || null);
    const [isCSMLoading, setIsCSMLoading] = useState(false);
    const [openRemoveCSMModal, setOpenRemoveCSMModal] = useState(false);
    const [csmChanged, setCSMChanged] = useState(false);
    const [formData, setFormData] = useState(() => ({
        lastContacted: client.lastContacted ? new Date(client.lastContacted) : null,
        lastCallAttended: client.lastCallAttended ? new Date(client.lastCallAttended) : null,
        lastInPersonEvent: client.lastInPersonEvent ? new Date(client.lastInPersonEvent) : null
    }));
    const [onboardingProgress, setOnboardingProgress] = useState("0%");
    const [initialOnboardingProgress, setInitialOnboardingProgress] = useState("0%");
    const [initialBillingStatus, setInitialBillingStatus] = useState("");
    const [initialIcp, setInitialIcp] = useState("");
    const [initialTouchpointFrequency, setInitialTouchpointFrequency] = useState("");
    const [initialFormData, setInitialFormData] = useState({});
    const [initialSelectedPrograms, setInitialSelectedPrograms] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isComponentLoading, setIsComponentLoading] = useState(true);
    const [ghlSynced, setGhlSynced] = useState(getGhlSyncedValue(client.ghlSynced));
    const [loadingStates, setLoadingStates] = useState({
        programs: true,
        csms: true,
        initialization: true
    });

    // const [circleConnected, setCircleConnected] = useState(false);
    // const [isMetricsLoading, setIsMetricsLoading] = useState(true);

    const navigate = useNavigate();
    const workspaceId = Auth.getWorkspaceId();
    const userId = Auth.getUser()?.id;
    const user = Auth.getUser();
    const isAdmin = user?.admin || false;

    const getStatus = (score) => {
        if (score >= 0 && score <= 3.99) return { color: 'at-risk', label: 'At Risk' };
        if (score >= 4.0 && score <= 6.99) return { color: 'fair', label: 'Fair' };
        if (score >= 7.0 && score <= 10.0) return { color: 'good', label: 'Good' };
        return { color: 'unknown', label: 'Unknown' };
    };

    const getRetentionScoreColor = (score) => {
        if (score >= 0 && score <= 3.99) return '#F52B53';
        if (score >= 4.0 && score <= 6.99) return '#F6A637';
        if (score >= 7.0 && score <= 10.0) return '#03BC72';
        return '#000000';
    };

    const calculateColorMain = (value) => {
        if (value >= 7) return { label: 'Great', color: '#03BC72' }; // Green
        if (value >= 5) return { label: 'Good', color: '#F6A637' }; // Orange
        return { label: 'At Risk', color: '#F52B53' }; // Red
    };

    useEffect(() => {
        setIcp(client.icp || '');
        setTouchpointFrequency(normalizeTouchpointFrequency(client.touchpointFrequency));
        setBillingStatus(client.billingStatus || '');
        setCompanyName(client.companyName || null);
        setClientRetentionScore(client.clientRetentionScore || null); 
        setSelectedPrograms((client.programs || []).map(p => p.id));
        setAssignedCSM(client.csmName || null);
        setOnboardingProgress(client.onboardingProgress || "0%");
        setGhlSynced(getGhlSyncedValue(client.ghlSynced));
        setInitialOnboardingProgress(client.onboardingProgress || "0%");
        setInitialBillingStatus(client.billingStatus || "");
        setInitialIcp(client.icp || "");
        setInitialTouchpointFrequency(normalizeTouchpointFrequency(client.touchpointFrequency));
        
        const initialDates = {
            lastContacted: client.lastContacted ? new Date(client.lastContacted) : null,
            lastCallAttended: client.lastCallAttended ? new Date(client.lastCallAttended) : null,
            lastInPersonEvent: client.lastInPersonEvent ? new Date(client.lastInPersonEvent) : null
        };
        setInitialFormData(initialDates);
        setInitialSelectedPrograms((client.programs || []).map(p => p.id));
        
        setIsInitialized(true);
        setLoadingStates(prev => ({ ...prev, initialization: true }));
    }, [client.id]);

    // Monitor all loading states
    useEffect(() => {
        const allLoaded = Object.values(loadingStates).every(state => !state);
        setIsComponentLoading(!allLoaded);
    }, [loadingStates]);

    useEffect(() => {
        setFormData({
            lastContacted: client.lastContacted ? new Date(client.lastContacted) : null,
            lastCallAttended: client.lastCallAttended ? new Date(client.lastCallAttended) : null,
            lastInPersonEvent: client.lastInPersonEvent ? new Date(client.lastInPersonEvent) : null
        });
        setOnboardingProgress(client.onboardingProgress || "0%");
        setInitialOnboardingProgress(client.onboardingProgress || "0%");
        setInitialBillingStatus(client.billingStatus || "");
        setInitialIcp(client.icp || "");
        setInitialTouchpointFrequency(normalizeTouchpointFrequency(client.touchpointFrequency));
        setIsInitialized(true);
        setLoadingStates(prev => ({ ...prev, initialization: false }));
    }, []);

    useEffect(() => {
        if (onboardingProgress !== "0%") {
            refreshClientData();
        }
    }, [onboardingProgress]);

    useEffect(() => {
        if (isOpen && client?.id && workspaceId) {
            refreshClientData();
        }
    }, [isOpen, client?.id]);

    // Prevent body scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
        
        // Cleanup function to remove class when component unmounts
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [isOpen]);

    useEffect(() => {
        const currentProgress = String(onboardingProgress);
        const initialProgress = String(initialOnboardingProgress);
        
        if (isInitialized && initialProgress !== "0%" && currentProgress !== initialProgress && currentProgress !== "0%") {
            setHasChanges(true);
        }
    }, [onboardingProgress, initialOnboardingProgress, isInitialized]);

    useEffect(() => {
        if (isInitialized && initialBillingStatus !== "" && billingStatus !== initialBillingStatus && billingStatus !== "") {
            setHasChanges(true);
        }
    }, [billingStatus, initialBillingStatus, isInitialized]);

    useEffect(() => {
        if (isInitialized && initialIcp !== "" && icp !== initialIcp && icp !== "") {
            setHasChanges(true);
        }
    }, [icp, initialIcp, isInitialized]);

    useEffect(() => {
        if (isInitialized && initialTouchpointFrequency !== "" && touchpointFrequency !== initialTouchpointFrequency && touchpointFrequency !== "") {
            setHasChanges(true);
        }
    }, [touchpointFrequency, initialTouchpointFrequency, isInitialized]);

    useEffect(() => {
        if (!isInitialized || !initialFormData || Object.keys(initialFormData).length === 0) return;
        
        const compareDates = (date1, date2) => {
            const d1 = (date1 instanceof Date) ? date1 : new Date(date1);
            const d2 = (date2 instanceof Date) ? date2 : new Date(date2);
            if (isNaN(d1.getTime()) && isNaN(d2.getTime())) return true;  
            if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return false;
            return d1.getTime() === d2.getTime();
        };
        
        const hasDateChanges = (
            !compareDates(formData.lastContacted, initialFormData.lastContacted) ||
            !compareDates(formData.lastCallAttended, initialFormData.lastCallAttended) ||
            !compareDates(formData.lastInPersonEvent, initialFormData.lastInPersonEvent)
        );
        
        if (hasDateChanges) {
            setHasChanges(true);
        }
    }, [formData, initialFormData, isInitialized]);

    useEffect(() => {
        if (!isInitialized) return;
        
        const programsChanged = (
            selectedPrograms.length !== initialSelectedPrograms.length ||
            selectedPrograms.some(id => !initialSelectedPrograms.includes(id)) ||
            initialSelectedPrograms.some(id => !selectedPrograms.includes(id))
        );
        
        if (programsChanged) {
            setHasChanges(true);
        }
    }, [selectedPrograms, initialSelectedPrograms, isInitialized]);

    useEffect(() => {
        const fetchPrograms = async () => {
            if (!workspaceId || !userId) {
                setLoadingStates(prev => ({ ...prev, programs: false }));
                return;
            }
            try {
                const { data } = await axios.get(`${API_URL}/programs/programs`, { params: { workspaceId, userId } });
                setAllPrograms(data);
            } catch (err) {
                setAllPrograms([]);
            } finally {
                setLoadingStates(prev => ({ ...prev, programs: false }));
            }
        };

        const fetchAvailableCSMs = async () => {
            if (!workspaceId || !userId || !isAdmin) {
                setLoadingStates(prev => ({ ...prev, csms: false }));
                return;
            }
            try {
                const response = await axios.get(
                    `${API_URL}/clients/available-csm?workspaceId=${workspaceId}&adminUserId=${userId}`
                );
                setAvailableCSMs(response.data);
            } catch (err) {
                console.error("Failed to fetch available CSMs:", err);
                setAvailableCSMs([]);
            } finally {
                setLoadingStates(prev => ({ ...prev, csms: false }));
            }
        };

        fetchPrograms();
        fetchAvailableCSMs();
    }, [workspaceId, userId, isAdmin]);

    // useEffect(() => {
    //     const checkCircleConnection = async () => {
    //         if (!workspaceId || !userId) {
    //             setLoadingStates((prev) => ({ ...prev, circle: false }));
    //             setIsMetricsLoading(false);
    //             return;
    //         }
    //         try {
    //             const circleResponse = await axios.get(
    //                 `${API_URL}/circle/circle-token?workspaceId=${workspaceId}&userId=${userId}`
    //             );
    //             setCircleConnected(circleResponse.data.hasBotToken || false);
    //         } catch (err) {
    //             console.error("Failed to fetch Circle.so integration status:", err);
    //             setCircleConnected(false);
    //         } finally {
    //             setIsMetricsLoading(false);
    //             setLoadingStates((prev) => ({ ...prev, circle: false }));
    //         }
    //     };
    //     checkCircleConnection();
    // }, [workspaceId, userId]);

    const refreshAvailableCSMs = async () => {
        if (!workspaceId || !userId || !isAdmin) return;
        try {
            const response = await axios.get(
                `${API_URL}/clients/available-csm?workspaceId=${workspaceId}&adminUserId=${userId}`
            );
            setAvailableCSMs(response.data);
        } catch (err) {
            console.error("Failed to refresh available CSMs:", err);
        }
    };

    const fetchClientRetentionScore = async (clientId) => {
        try {
            const response = await axios.get(`${API_URL}/clients/get-client-by-id`, {
                params: {
                    clientId: clientId,
                    workspaceId: workspaceId
                }
            });
            setClientRetentionScore(response.data.clientRetentionScore);
        } catch (error) {
            console.error('Error fetching client retention score:', error.message);
        }
    };

    const refreshClientData = async () => {
        try {
            const response = await axios.get(`${API_URL}/clients/get-client-by-id`, {
                params: {
                    clientId: client.id,
                    workspaceId: workspaceId,
                    includeCompany: true
                }
            });
            const updatedClient = response.data;
            
            setUpdatedClient(updatedClient);
            setClientRetentionScore(updatedClient.clientRetentionScore);
            setCompanyName(updatedClient.companyName);

            setGhlSynced(getGhlSyncedValue(updatedClient.ghlSynced));
            
            if (onClientUpdate) {
                onClientUpdate({
                    ...updatedClient,
                    formattedCRS: formatNumberWithTwoDecimals(updatedClient.clientRetentionScore),
                });
            }
        } catch (error) {
            console.error("Error refreshing client data:", error);
        }
    };

    if (!client) return null;

    const status = getStatus(clientRetentionScore); 

    const handleTouchpointFrequencyChange = async (event) => {
        const newFrequency = event.target.value;
        setTouchpointFrequency(newFrequency);

        if (!client.id || !newFrequency || !workspaceId) {
            console.error('Missing required data for touchpoint frequency update');
            return;
        }

        try {
            const response = await axios.put(
                `${API_URL}/clients/clients`,
                {
                    ...updatedClient, 
                    touchpointFrequency: newFrequency,
                    icp: icp,
                    workspaceId: workspaceId
                },
                {
                    headers: { 'Content-Type': 'application/json' },
                    params: { workspaceId: workspaceId }
                }
            );
            
            setUpdatedClient(response.data);
            fetchClientRetentionScore(client.id);
            await refreshClientData();
        } catch (error) {
            console.error('Error updating touchpoint frequency:', error.response?.data || error.message);
        }
    };

    const handleIcpStatusChange = async (event) => {
        const newIcp = event.target.value;
        setIcp(newIcp);

        if (!client.id || !newIcp || !workspaceId) {
            console.error('Missing required data for ICP update');
            return;
        }

        try {
            const response = await axios.put(
                `${API_URL}/clients/clients`,
                {
                    ...updatedClient,
                    icp: newIcp,
                    touchpointFrequency: touchpointFrequency,
                    workspaceId: workspaceId
                },
                {
                    headers: { 'Content-Type': 'application/json' },
                    params: { workspaceId: workspaceId }
                }
            );
            
            setUpdatedClient(response.data);
            fetchClientRetentionScore(client.id);
            await refreshClientData();
            
        } catch (error) {
            console.error('Error updating ICP status:', error.response?.data || error.message);
        }
    };

    const handleBillingStatusChange = async (event) => {
        const newBillingStatus = event.target.value;
        setBillingStatus(newBillingStatus);

        if (!client.id || !newBillingStatus || !workspaceId) {
            console.error('Missing required data for billing status update');
            return;
        }

        try {
            const response = await axios.put(
                `${API_URL}/clients/clients`,
                {
                    ...updatedClient,
                    billingStatus: newBillingStatus,
                    workspaceId: workspaceId
                },
                {
                    headers: { 'Content-Type': 'application/json' },
                    params: { workspaceId: workspaceId }
                }
            );

                        setUpdatedClient(response.data);
            fetchClientRetentionScore(client.id);
            await refreshClientData();
        } catch (error) {
            console.error('Error updating billing status:', error.response?.data || error.message);
        }
    };

    const handleProgramsChange = async (event) => {
        const value = event.target.value;
        
        if (value.length < selectedPrograms.length) {
            return;
        }
        
        const added = value.find(id => !selectedPrograms.includes(id));
        
        if (added) {
            try {
                await axios.post(`${API_URL}/programs/assign-program`, {
                    workspaceId: Number(workspaceId),
                    userId: Number(userId),
                    programId: Number(added),
                    clientId: Number(client.id)
                });
                setSelectedPrograms(value);
                toast.success('Program assigned successfully!');
            } catch (err) {
                console.error('Error assigning program:', err);
                alert('Error assigning program');
            }
        }
    };

    const handleRemoveProgram = async (programId) => {
        try {
            await axios.delete(`${API_URL}/programs/remove-program`, {
                params: {
                    workspaceId: Number(workspaceId),
                    userId: Number(userId),
                    programId: Number(programId),
                    clientId: Number(client.id)
                }
            });
            setSelectedPrograms(prev => prev.filter(id => id !== programId));
            toast.success('Program removed successfully!');
        } catch (err) {
            console.error('Error removing program:', err);
            alert('Error removing program');
        }
    };

    const handleDateChange = async (field, newDate) => {
        if (!client.id || !newDate || !workspaceId) {
            console.error("Missing required data for date update");
            return;
        }
        
        const isoDate = newDate.toISOString();
        setFormData(prev => ({ ...prev, [field]: newDate }));
        
        try {
            const currentDates = {
                lastContacted: formData.lastContacted ? formData.lastContacted.toISOString() : updatedClient.lastContacted,
                lastCallAttended: formData.lastCallAttended ? formData.lastCallAttended.toISOString() : updatedClient.lastCallAttended,
                lastInPersonEvent: formData.lastInPersonEvent ? formData.lastInPersonEvent.toISOString() : updatedClient.lastInPersonEvent
            };
            
            currentDates[field] = isoDate;
            
            const updatedClientData = {
                ...updatedClient,
                ...currentDates,
                workspaceId: workspaceId
            };
            
            const response = await axios.put(
                `${API_URL}/clients/clients`,
                updatedClientData,
                {
                    headers: { "Content-Type": "application/json" },
                    params: { workspaceId: workspaceId }
                }
            );
            
            toast.success(`${field.replace(/([A-Z])/g, ' $1').toLowerCase()} updated successfully!`);
            setUpdatedClient(response.data);
            
        } catch (error) {
            console.error(`Error updating ${field}:`, error.response?.data || error.message);
            toast.error(`Error updating ${field}`);
            setFormData(prev => ({ 
                ...prev, 
                [field]: client[field] ? new Date(client[field]) : null 
            }));
        }
    };

    const handleButtonClick = () => {
        navigate(`/client/${client.id}`, { state: { client: updatedClient } });
    };

    // const handleAdminPanelClick = () => {
    //     navigate('/admin-panel');
    // };

    const handleCSMAssignment = async (csmId) => {
        if (!csmId || isCSMLoading) return;
        
        setIsCSMLoading(true);
        try {
            const response = await axios.post(
                `${API_URL}/clients/assign-csm`,
                {
                    workspaceId: Number(workspaceId),
                    clientId: Number(client.id),
                    assignedUserId: Number(csmId),
                    adminUserId: Number(userId)
                },
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );
            const { firstName, lastName, email } = response.data.csmInfo;
            let csmDisplay = '';
            if (firstName && lastName) {
                csmDisplay = `${firstName} ${lastName}`;
            } else {
                csmDisplay = email;
            }
            setAssignedCSM(csmDisplay);
            setCSMChanged(true);
            toast.success('CSM assigned successfully!');
            await refreshAvailableCSMs();
            await refreshClientData();
        } catch (err) {
            console.error('Error assigning CSM:', err);
            toast.error('Error assigning CSM');
        } finally {
            setIsCSMLoading(false);
        }
    };

    const handleCSMRemoval = async () => {
        if (isCSMLoading) return;
        
        setIsCSMLoading(true);
        
        const payload = {
            workspaceId: Number(workspaceId),
            clientId: Number(client.id),
            adminUserId: Number(userId)
        };
        
        try {
            const response = await fetch(`${API_URL}/clients/remove-csm`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            
            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorData}`);
            }
            
            setAssignedCSM(null);
            setCSMChanged(true);
            toast.success('CSM removed successfully!');
            await refreshAvailableCSMs();
            await refreshClientData();
        } catch (err) {
            console.error('Error removing CSM:', err);
            toast.error('Error removing CSM');
        } finally {
            setIsCSMLoading(false);
        }
    };

    const handleCSMChange = async (event) => {
        const selectedValue = event.target.value;
        
        if (selectedValue) {
            await handleCSMAssignment(selectedValue);
        }
    };

    const handleClose = () => {
        if (hasChanges || csmChanged) {
            window.location.reload();
        }
        onClose();
    };

    const getBillingColor = (status) => {
        switch (status) {
            case 'Active':
                return { backgroundColor: '#0AC67D1F', textColor: '#03BC72' };
            case 'Paused':
                return { backgroundColor: '#F4C1071F', textColor: '#F6A637' };
            case 'Past Due':
                return { backgroundColor: '#FF00001F', textColor: '#F52B53' };
            default:
                return { backgroundColor: '#FFFFFF', textColor: '#000000' };
        }
    };

    const { backgroundColor, textColor } = getBillingColor(billingStatus);

    // Show loading state until all components are ready
    if (isComponentLoading) {
        return (
            <>
                <div className="client-details-overlay" onClick={handleClose}></div>
                <div className={`client-details-card client-details-card--loading ${isOpen ? 'open' : ''}`}>
                    <button className="close-btn" onClick={handleClose}>
                        ×
                    </button>
                    <div className="client-details-loading-body">
                        <div className="client-details-loading-spinner" aria-hidden="true" />
                        <p className="client-details-loading-text">Loading client details...</p>
                    </div>
                </div>
            </>
        );
    }

    return (
<>
    <div className="client-details-overlay" onClick={handleClose}></div>
        <div className={`client-details-card ${isOpen ? 'open' : ''}`}>
            <button className="close-btn" onClick={handleClose}>
                ×
            </button>
            <div style={{
                display: 'flex',
                width: '100%',
                justifyContent: 'space-between',
            }}>
                <div>
                    <h1>Client Details</h1>
                </div>
                <div>
                    <Button
                        className="purple-button"
                        sx={{
                            "&:hover": {
                                backgroundColor: "#4A21D1",
                            },
                        }}
                        style={{
                            display: 'inline-block',
                            marginTop: '15px',
                        }}
                        onClick={handleButtonClick}
                    >
                        Open
                    </Button>
                </div>
            </div>
            
            {/* Two Column Layout usando CSS Grid */}
            <div className="client-details-grid-container">
                {/* Left Column */}
                <div className="client-details-left-column">
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px' }}>
                <div style={{ flex: 1 }}>
                    <div className="client-name">
                        {`${client.firstName || 'Unknown'} ${client.lastName || ''}`.trim()}
                        <span className={`status ${status.color}`}>
                            ● {status.label}
                        </span>
                    </div>

                    {companyName && (
                        <div className="client-company-name" style={{ marginTop: '10px' }}>
                            @{companyName}
                        </div>
                    )}

                    <div className="client-usertag">
                        {client.userTag}
                    </div>
                </div>
                
                <div className="retention-chart-card">
                    <CircularProgressbarWithChildren
                        value={clientRetentionScore || 0}
                        maxValue={10}
                        circleRatio={0.5}
                        text={`${clientRetentionScore !== null && clientRetentionScore !== undefined ? clientRetentionScore.toFixed(2) : "0.00"}`}
                        styles={buildStyles({
                            textColor: '#291B3B',
                            textSize: '24px',
                            pathColor: calculateColorMain(clientRetentionScore || 0).color,
                            trailColor: '#EDECFD',
                            strokeLinecap: 'butt',
                            rotation: 0.75,
                        })}
                    >
                        <RadialSeparators
                            count={4}
                            circleRatio={0.5}
                            style={{
                                background: "#fff",
                                width: "2px",
                                height: "10%",
                            }}
                        />
                    </CircularProgressbarWithChildren>
                    <div className="retention-label-card">
                        <span className="retention-score-text-card">Client Retention Score</span>
                    </div>
                </div>
            </div>

            <div className="ICP-container-updated">
                <div className="dropdown-section">
                    <h2 style={{ marginBottom: '15px' }}>ICP Status</h2>
                    <FormControl
                        className="icp-dropdown"
                        sx={{
                            width: '100%',
                            backgroundColor: '#0AC67D1F',
                            borderRadius: '6.7px',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '20px',
                                '& fieldset': {
                                    border: 'none',
                                },
                            },
                        }}
                    >
                        <Select
                            value={icp}
                            onChange={handleIcpStatusChange}
                            label="Status"
                            sx={{
                                '& .MuiSelect-icon': {
                                    color: '#000',
                                },
                            }}
                        >
                            <MenuItem value="ICP I">ICP I</MenuItem>
                            <MenuItem value="ICP II">ICP II</MenuItem>
                            <MenuItem value="ICP III">ICP III</MenuItem>
                        </Select>
                    </FormControl>
                </div>
                <div className="dropdown-section">
                    <h2 style={{ marginBottom: '15px' }}>Touchpoint Frequency</h2>
                    <FormControl
                        className="touchpoint-dropdown"
                        sx={{
                            width: '100%',
                            backgroundColor: '#F4C1071F',
                            borderRadius: '6.7px',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '20px',
                                '& fieldset': {
                                    border: 'none',
                                },
                            },
                        }}
                    >
                        <Select
                            value={normalizeTouchpointFrequency(touchpointFrequency) || ""}
                            onChange={handleTouchpointFrequencyChange}
                            label="Frequency"
                            sx={{
                                '& .MuiSelect-icon': {
                                    color: '#000',
                                },
                            }}
                        >
                            <MenuItem value="Weekly">Weekly</MenuItem>
                            <MenuItem value="Bi-Weekly">Bi-Weekly</MenuItem>
                            <MenuItem value="Monthly">Monthly</MenuItem>
                        </Select>
                    </FormControl>
                </div>
                <div className="dropdown-section">
                    <h2 style={{ marginBottom: '15px' }}>Billing Status</h2>
                    <FormControl
                        className="billing-status-dropdown-header"
                        sx={{
                            width: '100%',
                            backgroundColor: backgroundColor,
                            borderRadius: '6.7px',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '20px',
                                '& fieldset': {
                                    border: 'none',
                                },
                            },
                        }}
                    >
                        <Select
                            value={billingStatus}
                            onChange={handleBillingStatusChange}
                            label="Billing Status"
                            sx={{
                                color: textColor,
                                '& .MuiSelect-icon': {
                                    color: '#000',
                                },
                            }}
                        >
                            <MenuItem value="Active" style={{ color: '#03BC72' }}>Active</MenuItem>
                            <MenuItem value="Paused" style={{ color: '#F6A637' }}>Paused</MenuItem>
                            <MenuItem value="Past Due" style={{ color: '#F52B53' }}>Past Due</MenuItem>
                        </Select>
                    </FormControl>
                </div>
            </div>
            {/* Program / Service Type Select */}
            <div style={{ marginBottom: 5, marginTop: 20 }}>
                        <FormControl sx={{ 
                            width: '100%',
                            maxWidth: '100%',
                            '& .MuiInputBase-root': {
                                width: '100%',
                                maxWidth: '100%' 
                            }
                }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            Program / Service Type
            <InfoTooltip
                title={
                    <span style={{ fontSize: "1.2rem", lineHeight: 1.5, color: "#fff" }}>
                        To add a new program,<br />
                        go to the Business Profile Configuration section in Admin<br />
                        and use the + Add Program button.
                    </span>
                }
                placement="top"
                iconSx={{ color: '#888', fontSize: 16, opacity: 0.7 }}
            />
            </span>
            <Select
                multiple
                value={selectedPrograms}
                onChange={handleProgramsChange}
                displayEmpty
                renderValue={() => {
                    if (selectedPrograms.length === 0) {
                        return "Program / Service Type";
                    }
                    const selectedProgramNames = allPrograms
                        .filter(p => selectedPrograms.includes(p.id))
                        .map(p => p.description);
                    
                    if (selectedProgramNames.length <= 2) {
                        return (
                            <span style={{ opacity: 0.7 }}>
                                {selectedProgramNames.join(', ')}
                            </span>
                        );
                    }
                    return (
                        <span style={{ opacity: 0.7 }}>
                            {selectedProgramNames.slice(0, 2).join(', ')} + {selectedProgramNames.length - 2} more
                        </span>
                    );
                }}
                sx={{ 
                    backgroundColor: "#f5f5f5",
                    width: '100%',
                    maxWidth: '100%', 
                    '& .MuiSelect-select': {
                        width: '100%',
                        maxWidth: '100%', 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        boxSizing: 'border-box' 
                    },
                    '& .MuiOutlinedInput-root': {
                        width: '100%',
                        maxWidth: '100%'
                    },
                    '& .MuiInputBase-root': {
                        width: '100%',
                        maxWidth: '100%'
                    }
                }}
            MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: 10 * 48,
                            width: 'auto', 
                            maxWidth: '400px'
                        },
                    },
                }}
            >
            <MenuItem disabled value="">
                Program / Service Type
            </MenuItem>
            {[
                ...allPrograms.filter(p => selectedPrograms.includes(p.id)),
                ...allPrograms.filter(p => !selectedPrograms.includes(p.id))
            ].map((program) => {
                const isSelected = selectedPrograms.includes(program.id);
                return (
                    <MenuItem
                        key={program.id}
                        value={program.id}
                        style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            opacity: isSelected ? 1 : 0.8,
                            maxWidth: '100%', 
                            overflow: 'hidden'
                        }}
                        onClick={isSelected ? (e) => e.preventDefault() : undefined}
                    >
                        {isSelected && (
                            <CheckCircleIcon style={{ color: '#03BC72', marginRight: 8 }} fontSize="small" />
                        )}
                        <span style={{ 
                            flex: 1, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap' 
                        }}>
                            {program.description}
                        </span>
                        {isSelected && (
                            <span
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginLeft: 8,
                                    borderRadius: '50%',
                                    transition: 'background 0.2s',
                                    cursor: 'pointer',
                                    width: 24,
                                    height: 24,
                                    flexShrink: 0 
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.background = '#ffeaea';
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.background = 'transparent';
                                }}
                                onClick={e => {
                                    e.stopPropagation();
                                    setProgramToRemove(program.id);
                                    setOpenRemoveModal(true);
                                }}
                            >
                                <CloseIcon
                                    style={{ color: '#F52B53' }}
                                    fontSize="small"
                                />
                            </span>
                        )}
                    </MenuItem>
                );
            })}
        </Select>
    </FormControl>
</div>
                    {/* Date Fields */}
                    <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
                        <div className="date-fields-section-card" style={{ width: '100%' }}>
                            <DateField
                                label="Last Contacted On"
                                value={formData.lastContacted}
                                onChange={(newDate) => handleDateChange('lastContacted', newDate)}
                            />
                            
                            <DateField
                                label="Last Call Attended"
                                value={formData.lastCallAttended}
                                onChange={(newDate) => handleDateChange('lastCallAttended', newDate)}
                            />
                            
                            <DateField
                                label="Last In-Person"
                                caption="This could mean Last In-Person Event, Last Service Rendered, or Last In-Person Visit depending on industry."
                                value={formData.lastInPersonEvent}
                                onChange={(newDate) => handleDateChange('lastInPersonEvent', newDate)}
                            />
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div className="client-details-right-column" style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    width: '100%',
                    paddingRight: '0px',
                    marginRight: '0px'
                }}>
                    <div style={{ width: '100%' }}>
                        {/* CSM Assignment Section - For administrators only*/}
                        {isAdmin && (
                            <div className="csm-assignment-section" style={{ marginBottom: '25px' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                    Assigned CSM
                                </span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FormControl 
                                        sx={{ 
                                            width: '100%',
                                            maxWidth: '100%',
                                            '& .MuiInputBase-root': {
                                                width: '100%',
                                                maxWidth: '100%' 
                                            }
                                        }}
                                    >
                                        <Select
                                            value=""
                                            onChange={handleCSMChange}
                                            displayEmpty
                                            disabled={isCSMLoading}
                                            renderValue={() => {
                                                const trimmedCSM = assignedCSM ? assignedCSM.trim() : '';
                                                if (trimmedCSM !== '' && trimmedCSM.toLowerCase() !== 'unassigned') {
                                                    return (
                                                        <span style={{ opacity: 0.7 }}>
                                                            {assignedCSM}
                                                        </span>
                                                    );
                                                }
                                                return "Select CSM";
                                            }}
                                            sx={{ 
                                                backgroundColor: "#f5f5f5",
                                                width: '100%',
                                                maxWidth: '100%', 
                                                '& .MuiSelect-select': {
                                                    width: '100%',
                                                    maxWidth: '100%', 
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    boxSizing: 'border-box' 
                                                },
                                                '& .MuiOutlinedInput-root': {
                                                    width: '100%',
                                                    maxWidth: '100%'
                                                },
                                                '& .MuiInputBase-root': {
                                                    width: '100%',
                                                    maxWidth: '100%'
                                                }
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 10 * 48,
                                                        width: 'auto', 
                                                        maxWidth: '400px'
                                                    },
                                                },
                                            }}
                                        >
                                            <MenuItem disabled value="">
                                                Select CSM
                                            </MenuItem>
                                            {availableCSMs.map((csm) => (
                                                <MenuItem 
                                                    key={csm.id} 
                                                    value={csm.id}
                                                    style={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        maxWidth: '100%', 
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    <span style={{ 
                                                        flex: 1, 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis', 
                                                        whiteSpace: 'nowrap' 
                                                    }}>
                                                        {csm.firstName && csm.lastName 
                                                            ? `${csm.firstName} ${csm.lastName}`
                                                            : csm.email}
                                                        <span style={{ 
                                                            marginLeft: '8px', 
                                                            fontSize: '0.875rem', 
                                                            color: '#666',
                                                            opacity: 0.7 
                                                        }}>
                                                            ({csm.clientCount} clients)
                                                        </span>
                                                    </span>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {(() => {
                                        const trimmedCSM = assignedCSM ? assignedCSM.trim() : '';
                                        return trimmedCSM !== '' && trimmedCSM.toLowerCase() !== 'unassigned';
                                    })() && (
                                        <span
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: '50%',
                                                transition: 'background 0.2s',
                                                cursor: 'pointer',
                                                width: 24,
                                                height: 24,
                                                flexShrink: 0 
                                            }}
                                            onMouseEnter={e => {
                                                e.currentTarget.style.background = '#ffeaea';
                                            }}
                                            onMouseLeave={e => {
                                                e.currentTarget.style.background = 'transparent';
                                            }}
                                            onClick={() => setOpenRemoveCSMModal(true)}
                                        >
                                            <CloseIcon
                                                style={{ color: '#F52B53' }}
                                                fontSize="small"
                                            />
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        <div style={{ marginTop: '0px', marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <h2 style={{ margin: 0 }}>Contact</h2>
                            <span style={{ 
                                display: 'inline-block',
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: ghlSynced ? '#03BC72' : '#F52B53',
                                marginLeft: '4px'
                            }}></span>
                            <span style={{ 
                                fontSize: '1rem',
                                color: ghlSynced ? '#03BC72' : '#F52B53',
                                fontWeight: 'normal'
                            }}>
                                {ghlSynced ? 'Synced with HighLevel' : 'Not Synced with HighLevel'}
                            </span>
                        </div>
                        <div className="contact-info" style={{ 
                            width: '100%',
                            display: 'grid',
                            gap: '20px'
                        }}>
                            <div style={{ width: '100%' }}>
                    <div className="attribute">
                        <span className="attribute-in">Email:</span>
                        <span className="contact-attribute">{client.email}</span>
                    </div>
                    <div className="attribute">
                        <span className="attribute-in">Phone:</span>
                        <span className="contact-attribute">{client.phoneNumber}</span>
                    </div>
                    <div className="attribute">
                        <span className="attribute-in">Address:</span>
                        <span className="contact-attribute">{client.address}</span>
                    </div>
                    <div className="attribute">
                        <span className="attribute-in">Onboarding:</span>
                        <span className="contact-attribute">{formatDate(client.onboardingDate)}</span>
                    </div>
                </div>
            </div>

                        <div className="onboarding-flow-container onboarding-flow-container--right">
                            <OnboardingFlow
                                clientId={client.id}
                                setOnboardingProgress={setOnboardingProgress}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Remove Program Modal */}
            <Dialog open={openRemoveModal} onClose={() => setOpenRemoveModal(false)}>
                <DialogTitle>Remove Program</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove this program from the client?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenRemoveModal(false)}>Cancel</Button>
                    <Button onClick={async () => {
                        await handleRemoveProgram(programToRemove);
                        setOpenRemoveModal(false);
                        setProgramToRemove(null);
                    }} color="error">Remove</Button>
                </DialogActions>
            </Dialog>

            {/* Remove CSM Modal */}
            <Dialog open={openRemoveCSMModal} onClose={() => setOpenRemoveCSMModal(false)}>
                <DialogTitle>Remove CSM Assignment</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove the current CSM assignment from this client?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenRemoveCSMModal(false)}>Cancel</Button>
                    <Button onClick={async () => {
                        await handleCSMRemoval();
                        setOpenRemoveCSMModal(false);
                    }} color="error">Remove</Button>
                </DialogActions>
            </Dialog>
        </div>
        </>
    );
};

export default ClientDetailsCard;